import * as React from 'react';
import {useSlate} from 'slate-react';

import {stringifyExpression} from '@packages/systems/wfdl';
import {
  type ContextualExpression,
  stripAll,
} from '@packages/utilities/contextual-expression';

import {styledDiv} from '@packages/utilities/styled';
import {serializeInput, deserializeExpressionToSlate} from './utils';
import {SlateTextUI, SlateContext} from './slateTextUI';
import {MenuWithDot} from './bindingMenuAndButton';
import {useAddToken} from './useAddToken';
import type {Expression} from '@packages/systems/wfdl-ast';

const fullWidth = {gridColumn: '1 / -1', position: 'relative'} as const;
const FullWidth = styledDiv(fullWidth);

type InterpolatedTextProps = {
  expr: ContextualExpression;
  onExpressionChange: (expr: Expression) => void;
  multiLine: boolean;
  placeholder: string;
  label: string;
};

export const InterpolatedText = React.memo<InterpolatedTextProps>(
  function InterpolatedText(props) {
    // In a useRef because this is called only in the very first render
    const getInitialSlateStateRef = React.useRef(() => {
      const editorValues = deserializeExpressionToSlate(props.multiLine)(
        stripAll(props.expr)
      );
      return editorValues;
    });

    // HACK: this is a workaround for a focus bug from SlateJS,
    // something to do with redundent re-renders requiring a user
    // to click twice in order to focus an input.
    // See https://github.com/ianstormtaylor/slate/issues/3880
    //
    // In this case, onExpressionChange appears to always not be referentially
    // equal because it's defined in the parent's function body without
    // memoization, so we're using `useRef` + `useCallback` combo here so
    // we can still pass down `onExpressionChange` as if it is an instance
    // method, while making sure we always have the latest version of
    // onExpressionChange by reassigning the ref in every render.
    const onExpressionChangeRef = React.useRef(props.onExpressionChange);
    onExpressionChangeRef.current = props.onExpressionChange;
    const _onExpressionChange = React.useCallback((nextExpr: Expression) => {
      onExpressionChangeRef.current(nextExpr);
    }, []);

    return (
      <SlateContext
        multiLine={props.multiLine}
        getInitialState={getInitialSlateStateRef.current}
        onExpressionChange={_onExpressionChange}
      >
        <InternalInterpolatedText
          {...props}
          onExpressionChange={_onExpressionChange}
        />
      </SlateContext>
    );
  }
);

export const InternalInterpolatedText = React.memo<InterpolatedTextProps>(
  function InternalInterpolatedText({
    expr,
    onExpressionChange,
    multiLine,
    placeholder,
    label,
  }) {
    const editor = useSlate();
    const onChange = React.useCallback(() => {
      // @ts-expect-error - TS2345 - Argument of type 'BaseEditor' is not assignable to parameter of type 'Editor'.
      onExpressionChange(serializeInput(editor));
    }, [editor, onExpressionChange]);
    const addToken = useAddToken(onChange);

    const onBindingChange = React.useCallback(
      // @ts-expect-error - TS7006 - Parameter 'binding' implicitly has an 'any' type.
      (binding) => {
        if (binding) {
          addToken(stringifyExpression(binding));
        }
      },
      [addToken]
    );

    return (
      <FullWidth>
        <SlateTextUI
          multiLine={multiLine}
          placeholder={placeholder || 'Enter text...'}
          expr={expr}
          onChange={onChange}
        />
        <MenuWithDot label={label} expr={expr} onChange={onBindingChange} />
      </FullWidth>
    );
  }
);
